import { useQuery, useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Input,
  Label,
  PageContainer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { arianeOrganisation } from "../../lib/ariane";

export function UserForm() {
  const { id: orgId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const org = useQuery(
    api.superAdmin.obtenirOrganisation,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const groupes = useQuery(
    api.superAdmin.listerGroupes,
    orgId ? { organisationId: orgId as Id<"organisations"> } : "skip",
  );
  const creerUser = useAction(api.superAdmin.creerEtProvisionnerUser);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [groupId, setGroupId] = useState(searchParams.get("groupId") ?? "");
  const [isGestionnaire, setIsGestionnaire] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!orgId) return;
    if (password !== passwordConfirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (!isGestionnaire && !groupId) {
      setError("Sélectionnez un groupe, ou cochez « Gestionnaire ».");
      return;
    }
    setError(null);
    setSaving(true);

    try {
      await creerUser({
        email,
        password,
        organisationId: orgId as Id<"organisations">,
        groupId: isGestionnaire ? undefined : (groupId as Id<"groups">),
        estGestionnaire: isGestionnaire,
      });
      void navigate(`/organisations/${orgId}/users`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <Breadcrumb
        items={[...arianeOrganisation(orgId, org?.nom, "users"), { label: "Nouvel utilisateur" }]}
      />
      <h1 className="text-2xl font-bold mb-6 text-center">
        Créer un utilisateur
      </h1>

      <form
        onSubmit={handleSubmit}
        className="bg-card border border-border rounded-surface p-6 space-y-4 max-w-xl mx-auto"
      >
        <div>
          <Label htmlFor="user-email" className="mb-1">
            Email
          </Label>
          <Input
            id="user-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
          />
        </div>

        <div>
          <Label htmlFor="user-password" className="mb-1">
            Mot de passe
          </Label>
          <Input
            id="user-password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8 caractères minimum"
          />
        </div>

        <div>
          <Label htmlFor="user-password-confirm" className="mb-1">
            Confirmer le mot de passe
          </Label>
          <Input
            id="user-password-confirm"
            type="password"
            required
            minLength={8}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder="Répéter le mot de passe"
          />
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="isGestionnaire"
            checked={isGestionnaire}
            onCheckedChange={(coche) => {
              const actif = coche === true;
              setIsGestionnaire(actif);
              if (actif) setGroupId("");
            }}
          />
          <label htmlFor="isGestionnaire" className="text-sm font-medium">
            Gestionnaire
          </label>
          <span className="text-xs text-muted-foreground">
            (accès à tous les parcours de l'organisation)
          </span>
        </div>

        {!isGestionnaire && (
          <div>
            {/* Le composant Select du design system n'est pas un <select>
                natif : `htmlFor` ne le désignerait pas. `aria-labelledby` sur le
                déclencheur est le seul rattachement qui fonctionne. */}
            <label id="user-groupe-label" className="block text-sm font-medium mb-1">
              Groupe
            </label>
            <Select
              required
              value={groupId || undefined}
              onValueChange={setGroupId}
              disabled={!groupes || groupes.length === 0}
            >
              <SelectTrigger className="w-full" aria-labelledby="user-groupe-label">
                <SelectValue placeholder="Sélectionner un groupe…" />
              </SelectTrigger>
              <SelectContent>
                {groupes?.map((g: any) => (
                  <SelectItem key={g._id} value={g._id}>
                    {g.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Obligatoire : le groupe délimite les parcours visibles par
              l'utilisateur. Pour un accès à toute l'organisation, cochez
              « Gestionnaire ».
            </p>
            {groupes?.length === 0 && (
              <p className="text-xs text-destructive mt-1">
                Aucun groupe dans cette organisation : créez-en un avant
                d'ajouter un utilisateur, ou cochez « Gestionnaire ».
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-controle px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving} className="flex-1">
            {saving ? "Création…" : "Créer l'utilisateur"}
          </Button>
          <Button asChild variant="outline">
            <Link to={`/organisations/${orgId}/users`}>Annuler</Link>
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
